import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { type QuickActionStatus, QuickActionStatusContext } from './QuickActionStatusContext';

/** Merges the markers domains publish, so neither this layer nor a list card knows which domain produced one. */
export function QuickActionStatusProvider({ children }: { children: ReactNode }) {
  const [bySource, setBySource] = useState<Readonly<Record<string, QuickActionStatus>>>({});

  // Explicitly stable, not left to the compiler: these are effect dependencies in
  // useQuickActionStatusRegistration, and a fresh identity there republishes in a loop.
  const publish = useCallback((source: string, nextStatus: QuickActionStatus) => {
    setBySource((prev) => (prev[source] === nextStatus ? prev : { ...prev, [source]: nextStatus }));
  }, []);

  const clear = useCallback((source: string) => {
    setBySource((prev) => {
      if (!(source in prev)) {
        return prev;
      }
      const { [source]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Merge in source-key order so which lazy chunk resolves first can't change the result.
  const status = Object.assign(
    {},
    ...Object.keys(bySource)
      .sort()
      .map((key) => bySource[key]),
  );
  const value = { status, publish, clear };

  return <QuickActionStatusContext.Provider value={value}>{children}</QuickActionStatusContext.Provider>;
}
